// Bloques que comparten las tarjetas de «Elige el teléfono» (por SKU y por modelo): precio con su
// descuento, la sección de crédito SureKeep y la fila de promos. Solo `span` para caber en un botón.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { cardCreditOf, type CardCredit, type LevelCreditTerms } from '../cardBenefits';
import type { BenefitBubble, CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { BenefitRow } from './BenefitRow';

const CARD_DIVIDER = 'border-t border-border/60 px-4';

type SureKeepCredit = Extract<CardCredit, { kind: 'surekeep' }>;

/** Precio a cobrar: el con descuento si de verdad baja el de lista. */
export function cardPriceOf(
    listCents: number,
    saleBenefits: CardSaleBenefits,
): { priceCents: number; hasDiscount: boolean } {
    const discounted = saleBenefits.discountedPriceCents;
    const hasDiscount = discounted !== null && discounted < listCents;
    return { priceCents: hasDiscount ? discounted : listCents, hasDiscount };
}

/** «Con SureKeep · enganche desde $X». */
function CreditLine({ credit }: { credit: SureKeepCredit }) {
    return (
        <span className="line-clamp-2 h-[31px] text-[12.5px] leading-[1.25] text-muted-foreground">
            Con SureKeep
            {credit.downPayment && (
                <>
                    {' · enganche desde '}
                    <strong className="font-semibold text-foreground tabular-nums">
                        {formatMXN(credit.downPayment.cents / 100)}
                    </strong>
                </>
            )}
        </span>
    );
}

/** Precio tachado, precio a cobrar y el renglón de contado (que reserva su alto aunque esté vacío). */
export function CardPrice({
    listCents,
    saleBenefits,
    note,
}: {
    listCents: number;
    saleBenefits: CardSaleBenefits;
    /** Texto extra del renglón de contado, p. ej. el SKU de la variante. */
    note?: ReactNode;
}) {
    const { priceCents, hasDiscount } = cardPriceOf(listCents, saleBenefits);
    const cash =
        saleBenefits.cashPriceCents !== undefined
            ? `De contado ${formatMXN(saleBenefits.cashPriceCents / 100)}`
            : null;
    return (
        <>
            <span
                data-testid="original-price"
                className="mt-2.5 min-h-[17px] text-[12px] text-muted-foreground tabular-nums line-through"
            >
                {hasDiscount ? formatMXN(listCents / 100) : null}
            </span>
            <span
                data-testid="price"
                className={cn(
                    'text-[24px] font-bold leading-tight tabular-nums',
                    hasDiscount && 'text-success',
                )}
            >
                {formatMXN(priceCents / 100)}
            </span>
            <span
                data-testid="cash-price"
                className="min-h-[17px] text-[12px] text-muted-foreground tabular-nums"
            >
                {cash}
                {cash !== null && note !== undefined && ' · '}
                {note}
            </span>
        </>
    );
}

/** Sección de crédito: solo con SureKeep confirmado y que lo financie. */
export function CardCreditSection({
    item,
    priceCents,
    creditTerms,
}: {
    item: CatalogItem;
    priceCents: number;
    creditTerms: LevelCreditTerms;
}) {
    const credit = cardCreditOf(item, priceCents, creditTerms);
    if (credit.kind !== 'surekeep') return null;
    return (
        <span
            data-section="credit"
            className={cn(CARD_DIVIDER, 'flex h-[60px] flex-col justify-center gap-0.5')}
        >
            <CreditLine credit={credit} />
        </span>
    );
}

/** Fila de promos; sin promos el hueco se queda para no mover el alto. Con `action`, va a la derecha. */
export function CardPromosSection({
    bubbles,
    action,
}: {
    bubbles: readonly BenefitBubble[];
    action?: ReactNode;
}) {
    const row = bubbles.length > 0 ? <BenefitRow bubbles={bubbles} /> : null;
    if (action === undefined) {
        return (
            <span
                data-section="promos"
                className={cn('block h-[45px] px-4 py-2.5', bubbles.length > 0 && CARD_DIVIDER)}
            >
                {row}
            </span>
        );
    }
    return (
        <span
            data-section="promos"
            className={cn('flex items-center gap-2 px-4 py-2.5', CARD_DIVIDER)}
        >
            <span className="min-w-0 flex-1">{row}</span>
            {action}
        </span>
    );
}
